'use client';

import type { IWordTiming } from '@repo/sdk';
import { type RefObject, useEffect, useState } from 'react';

export type IKaraokeMode = 'timings' | 'even' | 'inactive';

export interface IKaraokeActive {
  index: number;
  mode: IKaraokeMode;
}

const INACTIVE: IKaraokeActive = { index: -1, mode: 'inactive' };

export function useActiveWordIndex(
  audioRef: RefObject<HTMLAudioElement | null>,
  wordTimings: IWordTiming[],
  visibleWords: string[],
): IKaraokeActive {
  const [state, setState] = useState<IKaraokeActive>(INACTIVE);

  useEffect((): (() => void) | undefined => {
    const audio = audioRef.current;
    if (!audio || visibleWords.length === 0) {
      return undefined;
    }
    const useTimings = wordTimings.length > 0 && timingsAlignWithVisible(wordTimings, visibleWords);

    const update = (): void => {
      if (audio.paused) {
        setState(INACTIVE);
        return;
      }
      if (useTimings) {
        setState({
          index: findIndexByTimings(wordTimings, audio.currentTime * 1000),
          mode: 'timings',
        });
        return;
      }
      const duration = audio.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        setState({ index: -1, mode: 'even' });
        return;
      }
      const progress = Math.min(1, Math.max(0, audio.currentTime / duration));
      setState({
        index: Math.min(visibleWords.length - 1, Math.floor(progress * visibleWords.length)),
        mode: 'even',
      });
    };
    const reset = (): void => setState(INACTIVE);

    audio.addEventListener('timeupdate', update);
    audio.addEventListener('play', update);
    audio.addEventListener('ended', reset);
    audio.addEventListener('pause', reset);
    return (): void => {
      audio.removeEventListener('timeupdate', update);
      audio.removeEventListener('play', update);
      audio.removeEventListener('ended', reset);
      audio.removeEventListener('pause', reset);
    };
  }, [audioRef, wordTimings, visibleWords]);

  return state;
}

function timingsAlignWithVisible(timings: IWordTiming[], visibleWords: string[]): boolean {
  if (timings.length !== visibleWords.length) {
    return false;
  }
  for (let i = 0; i < timings.length; i++) {
    if (normalizeWord(timings[i]?.word ?? '') !== normalizeWord(visibleWords[i] ?? '')) {
      return false;
    }
  }
  return true;
}

function normalizeWord(raw: string): string {
  return raw.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();
}

function findIndexByTimings(timings: IWordTiming[], tMs: number): number {
  let lo = 0;
  let hi = timings.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const t = timings[mid];
    if (!t) {
      break;
    }
    if (t.startMs <= tMs) {
      result = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}
